import cx from 'classnames';
import PropTypes from 'prop-types';
import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {Colors} from '~/CreateFabricInvitation/components/Colors/Colors';
import {RecentColors} from '~/CreateFabricInvitation/components/Colors/RecentColors';
import {ColorUnit} from '~/CreateFabricInvitation/components/ColorUnit/ColorUnit';
import {EyeDropper} from '~/CreateFabricInvitation/components/EyeDropper/EyeDropper';
import {textColorOptions} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './ColorPicker.sass';

export const ColorPicker = ({
  recentColors = [],
  reduxKey,
  editorDisabled = false,
  color = '#ffffff',
  showColors = false,
  className = null,
  dataQaIdPrefix = 'color-picker',
  onChangeHandler,
  isolateDropper = false,
  showAll = false,
  isActive = false,
  highlight = false,
  selectedColor = null,
  recentColorLimit = 7,
  isTextColorPicker,
}) => {
  const dispatch = useDispatch();
  const isLargishAndUp = useIsLargishAndUp();
  const onInputColorChange = useCallback(
    ({target: {value}}) => onChangeHandler(value),
    [onChangeHandler]
  );

  const recentColorsMemo = useMemo(
    () => (isLargishAndUp ? recentColors : recentColors.slice(0, 3)),
    [recentColors, isLargishAndUp]
  );

  const wrapperClass = cx('color-picker__wrapper', className, {
    'color-picker__wrapper--dropper-only': isolateDropper,
    'color-picker__wrapper--mobile': !isLargishAndUp,
    'color-picker__wrapper--full': showAll,
    'color-picker__wrapper--active': isActive,
  });

  const colorSize = !isLargishAndUp ? 40 : 32;

  // If selected color is defined and the recentColors array doesn't already contain it,
  // add it to the array, remove any colors beyond the seventh in the array & store new array in redux
  const addToRecentColors = useCallback(() => {
    if (color !== undefined && !recentColors.includes(color)) {
      const newArray = [color, ...recentColors];

      while (newArray.length > recentColorLimit) {
        newArray.pop();
      }

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          [reduxKey]: newArray,
        },
      });
    }
  }, [color, recentColors, recentColorLimit, dispatch, reduxKey]);

  const renderRecentColors = () => (
    <>
      {recentColorsMemo.length > 0 && (
        <RecentColors
          size={colorSize}
          colors={recentColorsMemo}
          onClick={onChangeHandler}
          highlight={highlight}
          disabled={editorDisabled}
          isLargishAndUp={isLargishAndUp}
          selectedColor={selectedColor}
        />
      )}
      {showColors && (
        <Colors
          colors={textColorOptions}
          onClick={onChangeHandler}
          highlight={false}
          disabled={editorDisabled}
          height={colorSize}
          width={colorSize}
        />
      )}
    </>
  );

  if (!isLargishAndUp && !isTextColorPicker) {
    return (
      <>
        <EyeDropper
          color={color}
          onChange={onChangeHandler}
          onConfirmColor={addToRecentColors}
          editorDisabled={editorDisabled}
          className={className}
        />
        {renderRecentColors()}
      </>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="color-picker">
        {isLargishAndUp ? (
          <div className="color-picker__input-wrapper">
            <div className="color-picker__color-indicator">
              <ColorUnit
                color={editorDisabled ? '#6B5A3A' : color}
                key={color}
                height={colorSize}
                dataQaId={`${dataQaIdPrefix}-color-select-${color}`}
              />
            </div>
            <input
              className="color-picker__input"
              type="text"
              value={editorDisabled || !color ? '#' : color}
              onChange={onInputColorChange}
              disabled={editorDisabled}
              data-qa-id={`${dataQaIdPrefix}-color-input`}
            />
          </div>
        ) : null}
        <EyeDropper
          color={color}
          onChange={onChangeHandler}
          onConfirmColor={addToRecentColors}
          editorDisabled={editorDisabled}
          className={className}
        />
      </div>
      {renderRecentColors()}
    </div>
  );
};

ColorPicker.propTypes = {
  recentColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  reduxKey: PropTypes.string.isRequired,
  editorDisabled: PropTypes.bool,
  color: PropTypes.string,
  showColors: PropTypes.bool,
  className: PropTypes.string,
  dataQaIdPrefix: PropTypes.string,
  onChangeHandler: PropTypes.func.isRequired,
  isolateDropper: PropTypes.bool,
  showAll: PropTypes.bool,
  isActive: PropTypes.bool,
  highlight: PropTypes.bool,
  selectedColor: PropTypes.string,
  recentColorLimit: PropTypes.number,
};
